import React from 'react';
import { Navigate } from 'react-router-dom';
import Loadable, { lazyWithRetry } from '../layouts/full/shared/loadable/Loadable';

const ProjectListPage = Loadable(lazyWithRetry(() => import('../modules/projects/ProjectListPage')));
const ProjectFormPage = Loadable(lazyWithRetry(() => import('../modules/projects/ProjectFormPage')));
const ProjectDetailPage = Loadable(lazyWithRetry(() => import('../modules/projects/ProjectDetailPage')));
const ProjectAssignPage = Loadable(lazyWithRetry(() => import('../modules/projects/ProjectAssignPage')));

const TaskListPage = Loadable(lazyWithRetry(() => import('../modules/tasks/TaskListPage')));
const TaskKanbanPage = Loadable(lazyWithRetry(() => import('../modules/tasks/TaskKanbanPage')));
const TaskFormPage = Loadable(lazyWithRetry(() => import('../modules/tasks/TaskFormPage')));
const TaskDetailPage = Loadable(lazyWithRetry(() => import('../modules/tasks/TaskDetailPage')));
const PendingApprovalsPage = Loadable(lazyWithRetry(() => import('../modules/tasks/PendingApprovalsPage')));

const BugListPage = Loadable(lazyWithRetry(() => import('../modules/bugs/BugListPage')));
const BugCreatePage = Loadable(lazyWithRetry(() => import('../modules/bugs/BugCreatePage')));
const BugDetailPage = Loadable(lazyWithRetry(() => import('../modules/bugs/BugDetailPage')));
const BugEditPage = Loadable(lazyWithRetry(() => import('../modules/bugs/BugEditPage')));

const ClientListPage = Loadable(lazyWithRetry(() => import('../modules/clients/ClientListPage')));
const ClientFormPage = Loadable(lazyWithRetry(() => import('../modules/clients/ClientFormPage')));
const ClientDetailPage = Loadable(lazyWithRetry(() => import('../modules/clients/ClientDetailPage')));

const NotificationCenterPage = Loadable(
  lazyWithRetry(() => import('../modules/notifications/NotificationCenterPage')),
);
const AnalyticsPage = Loadable(lazyWithRetry(() => import('../modules/analytics/AnalyticsPage')));
const AiInsightsPage = Loadable(lazyWithRetry(() => import('../modules/ai/AiInsightsPage')));
const SettingsPage = Loadable(lazyWithRetry(() => import('../modules/settings/SettingsPage')));

const AdminPanelPage = Loadable(lazyWithRetry(() => import('../modules/admin/AdminPanelPage')));
const AdminUsersPage = Loadable(lazyWithRetry(() => import('../modules/admin/AdminUsersPage')));
const AdminBugQueuePage = Loadable(lazyWithRetry(() => import('../modules/admin/AdminBugQueuePage')));

const AttendanceCalendarPage = Loadable(lazyWithRetry(() => import('../modules/hr/AttendanceCalendarPage')));
const AttendanceHistoryPage = Loadable(lazyWithRetry(() => import('../modules/hr/AttendanceHistoryPage')));
const AdminAttendancePage = Loadable(lazyWithRetry(() => import('../modules/hr/AdminAttendancePage')));
const LeavePage = Loadable(lazyWithRetry(() => import('../modules/hr/LeavePage')));
const AdminLeaveApprovalsPage = Loadable(lazyWithRetry(() => import('../modules/hr/AdminLeaveApprovalsPage')));
const WfhPage = Loadable(lazyWithRetry(() => import('../modules/hr/WfhPage')));
const AdminWfhApprovalsPage = Loadable(lazyWithRetry(() => import('../modules/hr/AdminWfhApprovalsPage')));

const RenewalListPage = Loadable(lazyWithRetry(() => import('../modules/renewals/RenewalListPage')));
const RenewalFormPage = Loadable(lazyWithRetry(() => import('../modules/renewals/RenewalFormPage')));
const RenewalDetailPage = Loadable(lazyWithRetry(() => import('../modules/renewals/RenewalDetailPage')));

const PayslipListPage = Loadable(lazyWithRetry(() => import('../modules/payslips/PayslipListPage')));
const PayslipViewPage = Loadable(lazyWithRetry(() => import('../modules/payslips/PayslipViewPage')));
const AdminPayslipsPage = Loadable(lazyWithRetry(() => import('../modules/payslips/AdminPayslipsPage')));
const AdminPaymentInfoPage = Loadable(lazyWithRetry(() => import('../modules/admin/AdminPaymentInfoPage')));

/** Dashboard workspace routes (nested under AuthGuard + FullLayout). */
export const dashboardRouteChildren = [
  { path: '/dashboard/projects', element: <ProjectListPage /> },
  { path: '/dashboard/projects/new', element: <ProjectFormPage /> },
  { path: '/dashboard/projects/:projectId/edit', element: <ProjectFormPage /> },
  { path: '/dashboard/projects/:projectId/assign', element: <ProjectAssignPage /> },
  { path: '/dashboard/projects/:projectId/tasks/:taskId', element: <TaskDetailPage /> },
  { path: '/dashboard/projects/:projectId/bugs/:bugId', element: <BugDetailPage /> },
  { path: '/dashboard/projects/:projectId/bugs/:bugId/edit', element: <BugEditPage /> },
  { path: '/dashboard/projects/:projectId', element: <ProjectDetailPage /> },

  { path: '/dashboard/tasks', element: <TaskListPage /> },
  { path: '/dashboard/tasks/kanban', element: <TaskKanbanPage /> },
  { path: '/dashboard/tasks/new', element: <TaskFormPage /> },
  { path: '/dashboard/tasks/pending-approvals', element: <PendingApprovalsPage /> },

  { path: '/dashboard/bugs', element: <BugListPage /> },
  { path: '/dashboard/bugs/new', element: <BugCreatePage /> },

  { path: '/dashboard/clients', element: <ClientListPage /> },
  { path: '/dashboard/clients/new', element: <ClientFormPage /> },
  { path: '/dashboard/clients/:clientId/edit', element: <ClientFormPage /> },
  { path: '/dashboard/clients/:clientId', element: <ClientDetailPage /> },

  { path: '/dashboard/notifications', element: <NotificationCenterPage /> },
  { path: '/dashboard/analytics', element: <AnalyticsPage /> },
  { path: '/dashboard/ai-insights', element: <AiInsightsPage /> },
  { path: '/dashboard/settings', element: <SettingsPage /> },

  { path: '/dashboard/admin', element: <AdminPanelPage /> },
  { path: '/dashboard/admin/users', element: <AdminUsersPage /> },
  { path: '/dashboard/admin/approvals', element: <PendingApprovalsPage /> },
  { path: '/dashboard/admin/bugs', element: <AdminBugQueuePage /> },
  { path: '/dashboard/admin/attendance', element: <AdminAttendancePage /> },
  { path: '/dashboard/admin/leave', element: <AdminLeaveApprovalsPage /> },
  { path: '/dashboard/admin/wfh', element: <AdminWfhApprovalsPage /> },
  { path: '/dashboard/admin/payslips', element: <AdminPayslipsPage /> },
  { path: '/dashboard/admin/payment-info', element: <AdminPaymentInfoPage /> },

  { path: '/dashboard/attendance', element: <Navigate to="/dashboard/attendance/calendar" replace /> },
  { path: '/dashboard/attendance/calendar', element: <AttendanceCalendarPage /> },
  { path: '/dashboard/attendance/history', element: <AttendanceHistoryPage /> },
  { path: '/dashboard/leave', element: <LeavePage /> },
  { path: '/dashboard/wfh', element: <WfhPage /> },
  { path: '/dashboard/payslips', element: <PayslipListPage /> },
  { path: '/dashboard/payslips/:payslipId', element: <PayslipViewPage /> },

  { path: '/dashboard/renewals', element: <RenewalListPage /> },
  { path: '/dashboard/renewals/new', element: <RenewalFormPage /> },
  { path: '/dashboard/renewals/:renewalId', element: <RenewalDetailPage /> },

  { path: '/apps/clients', element: <Navigate to="/dashboard/clients" replace /> },
  { path: '/apps/settings', element: <Navigate to="/dashboard/settings" replace /> },
];
